// Adapter layer for the Terms & Policies page (FEATURE_030 (Terms & Policies)).
//
// Backend contract (live): GET /api/v1/medical/terms returns EVERY published term
// targeted at the medical channel, rendered dynamically (NOT a fixed 4-type list);
// the detail GET /api/v1/medical/terms/{typeId} is keyed by the numeric term id,
// because admin-added terms have no slug. Each row carries `required` (the wire
// field that backs the 필수/선택 type tag rendered on each list row (see TermsRow).
//
// Both branches share a single mock data source so the list / detail responses
// stay consistent end-to-end.

import { httpClient } from '@/api/http-client';
import { endpoints } from '@/api/endpoints';
import { ApiError, mapAxiosError } from '@/api/error';

// Field names mirror the BE DTOs (medical-api PublishedTermSummary / PublishedTerm) exactly.
// The BE terms API is slug-less and DYNAMIC — every admin-owned term type whose audience
// includes MEDICAL appears automatically once published, identified by numeric `typeId`.
export interface PublishedTermSummary {
  typeId: number;        // admin `ad_terms_type.id`; identifies the term on the detail path.
  name: string;          // type's Korean name (admin-owned; the only shipped locale).
  required: boolean;     // true = 필수 (gating), false = 선택 (opt-in). Drives the FE 필수/선택 tag.
  version: string;       // e.g. "v1.2"
  effectiveDate: string; // ISO YYYY-MM-DD
}

export interface PublishedTerm extends PublishedTermSummary {
  content: string;       // HTML, server-sanitized.
  registeredAt: string;  // ISO datetime
}

// The logged-in staff's own list row: the summary fields plus this account's `agreed` flag.
// Mirrors the medical-api StaffTermSummary DTO. `agreed` is scoped to the term's CURRENT published
// version, and is used ONLY to gate the [동의하기] opt-in (never rendered as an "agreed" status label).
export interface StaffTermSummary extends PublishedTermSummary {
  agreed: boolean;
}

interface ApiSuccess<T> {
  data: T;
}

const IS_MOCK = import.meta.env.VITE_USE_MOCK === 'true';
const MOCK_LATENCY_MS = 200;
const delay = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

// ──────────────────────────────────────────────────────────────────────────────
// Mock seeds — 4 default-seed terms with paragraph-length KR content (Q8: long
// enough to demonstrate scroll behavior end-to-end). Single source of truth
// for both list and detail responses. The list endpoint strips `content` and
// `registeredAt`; the detail endpoint (Slice 2) returns the full record.
//
// Field values for `typeId`, `name` mirror the admin contract seeds
// (admin-web/docs/api-contracts/term-management.md §2.2) so the BE swap is
// frictionless. Order intentionally matches admin (id ASC) — the page sorts
// to PRD §2.1 order on the FE side.
// ──────────────────────────────────────────────────────────────────────────────

const MOCK_TERMS: PublishedTerm[] = [
  {
    typeId: 1,
    name: '서비스 이용약관',
    required: true,
    version: 'v1.2',
    effectiveDate: '2026-04-01',
    registeredAt: '2026-03-21T01:24:00.000Z',
    content: `
      <h4>제1조 (목적)</h4>
      <p>본 약관은 울산대학교병원이 제공하는 스마트홈케어 서비스(이하 "서비스")의 이용과 관련하여 회사와 의료진 사이의 권리, 의무 및 책임사항, 기타 필요한 사항을 규정함을 목적으로 합니다.</p>
      <h4>제2조 (용어의 정의)</h4>
      <p>본 약관에서 사용하는 용어의 정의는 다음과 같습니다.</p>
      <ol>
        <li>"서비스"라 함은 회사가 의료진에게 제공하는 환자 관리, PRO 응답 모니터링, 위험도 평가 등 일체의 디지털 케어 서비스를 의미합니다.</li>
        <li>"의료진"이라 함은 회사와 서비스 이용계약을 체결하고 회사가 제공하는 서비스를 이용하는 의사, 간호사 등 의료 전문가를 말합니다.</li>
        <li>"계정"이라 함은 의료진의 식별과 서비스 이용을 위하여 회사가 부여한 이메일 주소 및 비밀번호의 조합을 의미합니다.</li>
      </ol>
      <h4>제3조 (약관의 효력 및 변경)</h4>
      <p>회사는 본 약관의 내용을 의료진이 쉽게 알 수 있도록 서비스 초기 화면에 게시합니다. 회사는 관련 법령을 위배하지 않는 범위에서 본 약관을 변경할 수 있으며, 약관이 변경되는 경우 적용일자 및 변경사유를 명시하여 시행일자 7일 이전부터 시행일자 전일까지 공지합니다.</p>
      <h4>제4조 (서비스의 제공 및 변경)</h4>
      <p>회사는 다음과 같은 업무를 수행합니다.</p>
      <ol>
        <li>담당 환자 등록 및 임상 정보 관리</li>
        <li>환자 PRO(Patient Reported Outcome) 응답 수집 및 모니터링</li>
        <li>위험도(R지수) 산출 및 등급 표시</li>
        <li>중재 기록 작성 및 보관</li>
        <li>기타 회사가 정하는 부가 업무</li>
      </ol>
      <h4>제5조 (의료진의 의무)</h4>
      <p>의료진은 다음 행위를 하여서는 안 됩니다.</p>
      <ol>
        <li>신청 또는 변경 시 허위 내용의 등록</li>
        <li>타인의 정보 도용</li>
        <li>회사가 게시한 정보의 무단 변경</li>
        <li>환자 개인정보의 무단 유출 또는 목적 외 이용</li>
        <li>기타 관련 법령에 위반되는 행위</li>
      </ol>
      <h4>제6조 (서비스 이용의 제한 및 중지)</h4>
      <p>회사는 의료진이 본 약관의 의무를 위반하거나 서비스의 정상적인 운영을 방해한 경우, 경고·일시정지·영구이용정지 등으로 서비스 이용을 단계적으로 제한할 수 있습니다.</p>
      <p>(데모 본문 — 실제 약관은 어드민에서 관리)</p>
    `.trim(),
  },
  {
    typeId: 2,
    name: '개인정보 수집 및 이용 동의',
    required: true,
    version: 'v1.1',
    effectiveDate: '2026-03-15',
    registeredAt: '2026-03-05T08:10:00.000Z',
    content: `
      <h4>1. 수집하는 개인정보 항목</h4>
      <p>울산대학교병원은 스마트홈케어 서비스 제공을 위하여 다음과 같은 개인정보를 수집합니다.</p>
      <ul>
        <li>필수 항목: 성명, 이메일 주소, 휴대전화번호, 소속 기관, 부서, 직무, 면허 정보</li>
        <li>선택 항목: 프로필 사진, 비상 연락처</li>
        <li>자동 수집 항목: 접속 IP, 쿠키, 서비스 이용 기록, 접속 로그, 단말기 정보(OS, 화면 해상도, 디바이스 고유 식별자)</li>
      </ul>
      <h4>2. 개인정보의 수집 및 이용 목적</h4>
      <p>수집된 개인정보는 다음의 목적을 위해 활용됩니다.</p>
      <ol>
        <li>회원 식별 및 본인 확인</li>
        <li>서비스 제공 및 운영, 환자 케어 의료진 연결</li>
        <li>고지사항 전달, 민원처리 등 의사소통 경로 확보</li>
        <li>서비스 부정이용 방지 및 보안 사고 대응</li>
        <li>법령 또는 이용약관에 위반된 행위 제재 등</li>
      </ol>
      <h4>3. 개인정보의 보유 및 이용 기간</h4>
      <p>회사는 의료진의 개인정보를 원칙적으로 회원 탈퇴 시까지 보유·이용합니다. 단, 관련 법령에 의해 보존할 필요가 있는 경우에는 해당 기간 동안 보관합니다.</p>
      <ul>
        <li>접속 로그: 통신비밀보호법에 따라 3개월</li>
        <li>표시·광고에 관한 기록: 전자상거래법에 따라 6개월</li>
        <li>계약 또는 청약철회 등에 관한 기록: 전자상거래법에 따라 5년</li>
        <li>소비자 불만 또는 분쟁처리에 관한 기록: 전자상거래법에 따라 3년</li>
      </ul>
      <h4>4. 동의 거부 권리 및 거부 시 불이익</h4>
      <p>의료진은 개인정보 수집 및 이용에 동의를 거부할 권리가 있습니다. 다만, 필수 항목 동의를 거부하는 경우 회원 가입 및 서비스 이용이 제한됩니다.</p>
      <p>(데모 본문 — 실제 약관은 어드민에서 관리)</p>
    `.trim(),
  },
  {
    typeId: 3,
    name: '민감정보 수집 및 이용 동의',
    required: true,
    version: 'v1.0',
    effectiveDate: '2026-01-15',
    registeredAt: '2026-01-05T02:00:00.000Z',
    content: `
      <h4>1. 민감정보의 정의 및 수집 항목</h4>
      <p>"민감정보"란 개인정보 보호법 제23조에 따른 사상·신념, 정치적 견해, 건강에 관한 정보 등 정보주체의 사생활을 현저히 침해할 우려가 있는 개인정보를 말합니다. 본 서비스는 다음의 민감정보를 수집합니다.</p>
      <ul>
        <li>정신건강 진단명 및 진단코드 (ICD-10 기반)</li>
        <li>처방 약물 및 복약 이력</li>
        <li>PRO 응답을 통해 수집되는 우울감, 자살 사고, 수면 패턴 등 정신건강 관련 자가 보고 데이터</li>
        <li>위험도 평가 결과 및 등급</li>
        <li>상담 및 중재 기록</li>
      </ul>
      <h4>2. 민감정보의 수집 및 이용 목적</h4>
      <p>수집된 민감정보는 다음의 목적을 위해서만 활용됩니다.</p>
      <ol>
        <li>정신건강 사례 관리 및 위험 신호 조기 발견</li>
        <li>PRO 응답 분석을 통한 임상 의사결정 지원</li>
        <li>맞춤형 중재 계획 수립 및 보호자 교육 자료 제공</li>
        <li>치료 효과 평가 및 임상 통계 산출 (가명처리 후 활용)</li>
      </ol>
      <h4>3. 민감정보의 보유 및 이용 기간</h4>
      <p>수집된 민감정보는 의료법 제22조에 따른 의료 기록 보존 기간(10년)에 준하여 보관되며, 보존 기간 경과 시 지체 없이 파기합니다. 단, 정보주체의 동의가 있거나 법령에 특별한 규정이 있는 경우에는 해당 기간 동안 보관합니다.</p>
      <h4>4. 민감정보의 제3자 제공</h4>
      <p>회사는 원칙적으로 정보주체의 별도 동의 없이는 민감정보를 제3자에게 제공하지 않습니다. 다만, 다음의 경우에는 예외로 합니다.</p>
      <ul>
        <li>정보주체의 사전 동의가 있는 경우</li>
        <li>법령에 특별한 규정이 있거나 법령상 의무를 준수하기 위해 불가피한 경우</li>
        <li>정보주체 또는 그 법정대리인이 의사표시를 할 수 없는 상태에 있어 미리 동의를 받을 수 없는 경우로서 명백히 정보주체 또는 제3자의 급박한 생명, 신체, 재산의 이익을 위해 필요한 경우</li>
      </ul>
      <h4>5. 동의 거부 권리 및 거부 시 불이익</h4>
      <p>의료진은 민감정보 수집 및 이용에 동의를 거부할 권리가 있습니다. 다만, 동의를 거부하는 경우 정신건강 케어 서비스의 핵심 기능 이용이 제한됩니다.</p>
      <p>(데모 본문 — 실제 약관은 어드민에서 관리)</p>
    `.trim(),
  },
  {
    typeId: 4,
    name: '개인정보 처리방침',
    required: true,
    version: 'v1.3',
    effectiveDate: '2026-05-01',
    registeredAt: '2026-04-15T05:00:00.000Z',
    content: `
      <h4>제1조 (개인정보의 처리 목적)</h4>
      <p>울산대학교병원(이하 "병원")은 개인정보 보호법 제30조에 따라 정보주체의 개인정보를 보호하고 이와 관련한 고충을 신속하고 원활하게 처리할 수 있도록 하기 위하여 다음과 같이 개인정보 처리방침을 수립·공개합니다.</p>
      <h4>제2조 (개인정보의 처리 및 보유 기간)</h4>
      <p>병원은 법령에 따른 개인정보 보유·이용기간 또는 정보주체로부터 개인정보를 수집 시에 동의받은 개인정보 보유·이용기간 내에서 개인정보를 처리·보유합니다.</p>
      <h4>제3조 (정보주체와 법정대리인의 권리·의무 및 행사방법)</h4>
      <p>정보주체는 병원에 대해 언제든지 다음 각 호의 개인정보 보호 관련 권리를 행사할 수 있습니다.</p>
      <ol>
        <li>개인정보 열람 요구</li>
        <li>오류 등이 있을 경우 정정 요구</li>
        <li>삭제 요구</li>
        <li>처리정지 요구</li>
      </ol>
      <h4>제4조 (개인정보의 안전성 확보 조치)</h4>
      <p>병원은 개인정보의 안전성 확보를 위해 다음과 같은 조치를 취하고 있습니다.</p>
      <ul>
        <li>개인정보 취급 직원의 최소화 및 교육</li>
        <li>접근권한의 제한조치 및 접근통제시스템 설치</li>
        <li>개인정보의 암호화</li>
        <li>해킹 등에 대비한 기술적·관리적 대책</li>
        <li>비인가자에 대한 출입 통제</li>
      </ul>
      <h4>제5조 (개인정보 보호책임자)</h4>
      <p>병원은 개인정보 처리에 관한 업무를 총괄해서 책임지고, 개인정보 처리와 관련한 정보주체의 불만처리 및 피해구제 등을 위하여 아래와 같이 개인정보 보호책임자를 지정하고 있습니다.</p>
      <ul>
        <li>개인정보 보호책임자: 울산대학교병원 의료정보부장</li>
        <li>연락처: 052-250-7000</li>
      </ul>
      <h4>제6조 (개인정보 처리방침의 변경)</h4>
      <p>이 개인정보 처리방침은 시행일로부터 적용되며, 법령 및 방침에 따른 변경내용의 추가, 삭제 및 정정이 있는 경우에는 변경사항의 시행 7일 전부터 공지사항을 통하여 고지할 것입니다.</p>
      <p>(데모 본문 — 실제 약관은 어드민에서 관리)</p>
    `.trim(),
  },
  {
    typeId: 5,
    name: '마케팅 정보 수신 동의',
    version: 'v1.0',
    effectiveDate: '2026-01-01',
    required: false,
    registeredAt: '2025-12-20T03:00:00.000Z',
    content: `
      <h4>1. 수신 항목</h4>
      <p>신규 기능 안내, 이벤트 및 만족도 조사 등 회사가 제공하는 마케팅 정보를 수신합니다.</p>
      <h4>2. 수신 방법</h4>
      <p>이메일, 앱 푸시 알림</p>
      <h4>3. 동의 거부 권리 및 거부 시 불이익</h4>
      <p>마케팅 정보 수신 동의는 선택 사항이며, 동의하지 않아도 서비스 이용에는 어떠한 영향도 없습니다. 동의 이후에도 언제든지 수신을 거부할 수 있습니다.</p>
      <p>(데모 본문 — 실제 약관은 어드민에서 관리)</p>
    `.trim(),
  },
];

function toSummary(term: PublishedTerm): PublishedTermSummary {
  const { content: _content, registeredAt: _registeredAt, ...summary } = term;
  void _content;
  void _registeredAt;
  return summary;
}

// ──────────────────────────────────────────────────────────────────────────────
// GET /api/v1/medical/terms
// ──────────────────────────────────────────────────────────────────────────────
export async function fetchPublishedTermsList(): Promise<PublishedTermSummary[]> {
  if (IS_MOCK) {
    await delay(MOCK_LATENCY_MS);
    return MOCK_TERMS.map(toSummary);
  }
  try {
    const { data } = await httpClient.get<ApiSuccess<PublishedTermSummary[]>>(
      endpoints.medicalStaff.terms,
    );
    return data.data;
  } catch (err) {
    throw mapAxiosError(err);
  }
}

// ──────────────────────────────────────────────────────────────────────────────
// GET /medical/terms/{typeId}
// ──────────────────────────────────────────────────────────────────────────────
export async function fetchPublishedTerm(typeId: number): Promise<PublishedTerm> {
  if (IS_MOCK) {
    await delay(MOCK_LATENCY_MS);
    const hit = MOCK_TERMS.find((t) => t.typeId === typeId);
    if (!hit) {
      throw new ApiError('NOT_FOUND', '요청한 약관을 찾을 수 없습니다.');
    }
    return hit;
  }
  try {
    const { data } = await httpClient.get<ApiSuccess<PublishedTerm>>(
      endpoints.medicalStaff.termByTypeId(typeId),
    );
    return data.data;
  } catch (err) {
    throw mapAxiosError(err);
  }
}

// ──────────────────────────────────────────────────────────────────────────────
// GET /api/v1/medical/me/terms — the logged-in staff's list, with `agreed` per term
//
// The /settings (Terms & Policies) page reads this instead of the public `terms` list,
// because only an authenticated request has the caller identity needed to know which
// optional terms this staff has already agreed to (and so which need [동의하기]).
// ──────────────────────────────────────────────────────────────────────────────
export async function fetchStaffTermsList(): Promise<StaffTermSummary[]> {
  if (IS_MOCK) {
    await delay(MOCK_LATENCY_MS);
    const agreedIds = new Set(readAgreedOptionalTypeIds());
    return MOCK_TERMS.map((t) => ({ ...toSummary(t), agreed: agreedIds.has(t.typeId) }));
  }
  try {
    const { data } = await httpClient.get<ApiSuccess<StaffTermSummary[]>>(
      endpoints.medicalStaff.myTerms,
    );
    return data.data;
  } catch (err) {
    throw mapAxiosError(err);
  }
}

// ──────────────────────────────────────────────────────────────────────────────
// Optional-term consent (opt-in)
//
// Real mode: POST /api/v1/medical/terms/{typeId}/agree records the opt-in server-side;
// the caller then refetches GET /me/terms so `agreed` comes from the server (the source
// of truth), which is what removes the [동의하기] button for that row.
// Mock mode: opt-in state is persisted client-side in sessionStorage so the prototype
// flow (settings.html ↔ terms-view.html) still works without a backend.
// ──────────────────────────────────────────────────────────────────────────────
const CONSENT_STORAGE_KEY = 'agreedOptionalTerms';

export function readAgreedOptionalTypeIds(): number[] {
  try {
    const raw = sessionStorage.getItem(CONSENT_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as number[]) : [];
  } catch {
    return [];
  }
}

export async function agreeOptionalTerm(typeId: number): Promise<void> {
  if (IS_MOCK) {
    await delay(MOCK_LATENCY_MS);
    const agreed = readAgreedOptionalTypeIds();
    if (!agreed.includes(typeId)) {
      sessionStorage.setItem(
        CONSENT_STORAGE_KEY,
        JSON.stringify([...agreed, typeId]),
      );
    }
    return;
  }
  try {
    await httpClient.post(endpoints.medicalStaff.termAgree(typeId));
  } catch (err) {
    throw mapAxiosError(err);
  }
}
